import { folderDestinations, reorder as reorderList } from '@church/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { servicesKeys } from './useServices.ts'

/**
 * Folders of service types (docs/13-ACCESS.md): "Main campus" › "Youth",
 * as deep as the church likes. The API keeps the rules (`folderPlacementError` in
 * @church/domain); these hooks read the tree and change it. The Plans page
 * drills into it with `useServiceFolderLevel` (usePlansPage.ts).
 */

export function useServiceFolders(options: { enabled?: boolean } = {}) {
  const api = useApi()
  return useQuery({
    queryKey: servicesKeys.folders(),
    enabled: options.enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.folders.get()
      if (error) throw error
      return data
    },
  })
}

export type ServiceFolder = NonNullable<ReturnType<typeof useServiceFolders>['data']>[number]

/**
 * Changing the folders: add, rename, move (into another folder or the top
 * level), reorder siblings, delete (what was in it moves up a level), and
 * put a service type in one. Every change refreshes the folders and the
 * service types (whose `folderId` a delete changes).
 */
export function useServiceFolderEditor() {
  const api = useApi()
  const queryClient = useQueryClient()
  const folders = useServiceFolders()
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: servicesKeys.folders() })
    void queryClient.invalidateQueries({ queryKey: servicesKeys.types() })
  }
  const setTree = (data: ServiceFolder[] | null | undefined) => {
    if (data) queryClient.setQueryData(servicesKeys.folders(), data)
  }

  const create = useMutation({
    mutationFn: async (body: { name: string; parentId: string | null }) => {
      const { data, error } = await api.api.v1.services.folders.post(body)
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })
  const update = useMutation({
    mutationFn: async ({
      id,
      ...body
    }: {
      id: string
      name?: string
      /** Its new parent; null for the top level. */
      parentId?: string | null
    }) => {
      const { data, error } = await api.api.v1.services.folders({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: setTree,
  })
  const reorder = useMutation({
    mutationFn: async (body: { parentId: string | null; ids: string[] }) => {
      const { data, error } = await api.api.v1.services.folders.order.put(body)
      if (error) throw error
      return data
    },
    onSuccess: setTree,
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.services.folders({ id }).delete()
      if (error) throw error
    },
    onSuccess: refresh,
  })
  const setTypeFolder = useMutation({
    mutationFn: async ({ id, folderId }: { id: string; folderId: string | null }) => {
      const { data, error } = await api.api.v1.services['service-types']({ id }).folder.put({
        folderId,
      })
      if (error) throw error
      return data
    },
    onSuccess: refresh,
  })

  const list = folders.data ?? []
  /** One place up (-1) or down (+1) among its siblings. */
  const move = (id: string, by: -1 | 1) => {
    const folder = list.find((candidate) => candidate.id === id)
    if (!folder) return
    const siblings = list.filter((candidate) => candidate.parentId === folder.parentId)
    const from = siblings.findIndex((candidate) => candidate.id === id)
    const to = from + by
    if (from < 0 || to < 0 || to >= siblings.length) return
    reorder.mutate({
      parentId: folder.parentId,
      ids: reorderList(siblings, from, to).map((candidate) => candidate.id),
    })
  }
  /** Where a folder could go (none inside itself); all of them for a new one. */
  const destinations = (id?: string) => folderDestinations(list, id)
  /** Its place among its siblings, and how many there are. */
  const placeOf = (id: string) => {
    const folder = list.find((candidate) => candidate.id === id)
    const siblings = list.filter((candidate) => candidate.parentId === folder?.parentId)
    return { index: siblings.findIndex((candidate) => candidate.id === id), count: siblings.length }
  }

  return { create, update, reorder, remove, setTypeFolder, move, destinations, placeOf }
}
